/* Zugriff auf die API.
 *
 * Betraege sind hier ueberall `string`. Das ist Absicht und keine
 * Nachlaessigkeit: die API gibt sie als Zeichenkette heraus, damit im
 * Browser kein Cent verlorengeht. Wer rechnen will, tut das im Server.
 */

export type Betrag = string

export interface Aenderung {
  datum: string
  art: 'NEU' | 'VERBESSERT' | 'BEHOBEN'
  titel: string
  bereich: string
  text: string
}

export interface Auswertung {
  schluessel: string
  name: string
  beschreibung: string
  ansicht: 'tag' | 'artikel' | 'quoten' | 'stationen' | 'abbrueche'
  werkzeug: 'REPORTING' | 'AUDIT'
  quellen: string[]
  faehigkeiten: {
    darstellung: ('tabelle' | 'balken' | 'linie' | 'anteil')[]
    breite: ('viertel' | 'halb' | 'voll')[]
    zeitachse: boolean
    dimensionen: string[]
    nullzeilen: boolean
    grenze: boolean
  }
  voreinstellung: { aktiv: boolean; reihenfolge: number; breite: 'viertel' | 'halb' | 'voll' }
  aktiv: boolean
  reihenfolge: number
  breite: 'viertel' | 'halb' | 'voll'
  abweichend: boolean
}

export interface Auswertungsansicht {
  schluessel: 'tag' | 'artikel' | 'quoten' | 'stationen' | 'abbrueche'
  name: string
  beschreibung: string
  werkzeuge: ('REPORTING' | 'AUDIT')[]
  voreinstellung: { aktiv: boolean }
  aktiv: boolean
  abweichend: boolean
}

export interface Standort {
  id: string
  code: string
  name: string
  active: boolean
  letzter_tag: string | null
}

export interface Kontext {
  mandant: {
    code: string
    name: string
    module_dashboard: boolean
    module_audit: boolean
    dimension_employee: boolean
  }
  betriebe: Standort[]
  zeitraum: { von: string | null; bis: string | null; tage: number }
}

export interface Kennzahlen {
  umsatz: Betrag
  umsatz_bereinigt: Betrag
  bereinigung_mitarbeiter: Betrag
  bereinigung_bruch: Betrag
  rechnungen: number
  tischbesuche: number
  durchschnittsbon: Betrag
  umsatz_je_tischbesuch: Betrag
  trinkgeld: Betrag
  trinkgeldquote: number | null
  betriebe: number
  tage: number
}

export interface Vergleich {
  heute: Kennzahlen
  vortag: Kennzahlen
  vorwoche: Kennzahlen
  delta_vortag: number | null
  delta_vorwoche: number | null
}

export interface Stunde { stunde: number; umsatz: Betrag; rechnungen: number }
export interface Tag {
  tag: string; umsatz: Betrag; rechnungen: number
  durchschnittsbon: Betrag
  ampel: string | null
}
export interface Wochentag {
  wochentag: number; umsatz: Betrag; rechnungen: number; tage: number
}
export interface Zahlart {
  zahlart: string; art: string; art_quelle?: string
  umsatz: Betrag; anzahl: number; kassennamen: number
}
export interface UmsatzZahlart extends Zahlart {
  trinkgeld: Betrag
}
export interface Warengruppe {
  warengruppe: string; art: string; umsatz: Betrag
  menge: Betrag; positionen: number
}
export interface Artikel {
  artikel: string; warengruppe_code: string | null
  menge: Betrag; umsatz: Betrag
  auf_bons: number | null; stornos?: number; sofortstornos?: number
}
export interface Rabattart { art: string; summe: Betrag; anzahl: number }
export interface Stornos {
  zurueckgenommen_summe: Betrag; zurueckgenommen_anzahl: number
  zurueckgenommen_hinweis: string | null
  rabatt_summe: Betrag; rabatt_anzahl: number
  rabatt_gesamt: Betrag
  rabatt_toepfe: { rabatte: Betrag; maison: Betrag; verlust_bruch: Betrag;
                   abweichung: Betrag } | null
  sofort_wert: Betrag; sofort_anzahl: number
  gebucht: Betrag; stornoquote: number | null
  rabattarten: Rabattart[]
}
export interface AmpelTag {
  tag: string; betrieb: string; betrieb_name: string
  ampel: string | null
  quellen: string[]; fehlend: string[]; berechnet: string | null
  /** Ausfallzahlungen dieses Standort-Tages — netto gegen Storno
   *  gerechnet, nicht als Kopfzahl. 0 heisst „keine", nicht
   *  „nicht gemessen". */
  ausfall_anzahl: number; ausfall_betrag: Betrag
}
export interface Pruefung {
  code: string; label: string; severity: 'SUM' | 'META'
  einheit: 'EUR' | 'ANZAHL'
  soll: Betrag | null; ist: Betrag | null
  soll_text: string | null; ist_text: string | null
  bestanden: boolean; hinweis: string | null
}

/** Speise, Getränk oder Sonstiges — aus der Statistikgruppe des
 *  Artikels, ersatzweise aus seiner Warengruppe. */
export interface Speisenart {
  art: 'FOOD' | 'BEVERAGE' | 'OTHER' | 'UNKNOWN'
  umsatz: Betrag
  menge: Betrag
  positionen: number
  /** Auf wie vielen Tischbelegungen die Art vorkam — nicht auf wie vielen
   *  Bons: Ein Tisch bestellt mehrmals. Für lange Zeiträume null, weil
   *  die Zählung dort zu teuer ist. */
  tischbesuche: number | null
}
export interface SpeisenGetraenke {
  arten: Speisenart[]
  umsatz: Betrag
  tischbesuche_gesamt: number | null
  /** Umsatz ohne Zuordnung. Null heißt: alles ist zugeordnet. */
  ohne_zuordnung: Betrag | null
}

/* Warenkorb: was auf derselben Tischbelegung zusammen bestellt wurde. */
export interface KorbSache {
  schluessel: string
  name: string
  /** In wie vielen Körben die Sache lag. */
  koerbe: number
  /** Davon Körbe, auf denen auch eine Speise lag. */
  mit_speise: number
}
export interface KorbPaar {
  a: string; b: string
  name_a: string; name_b: string
  zusammen: number
  koerbe_a: number; koerbe_b: number
  /** Wie oft beide zusammen vorkämen, wenn sie nichts miteinander zu
   *  tun hätten. */
  erwartet: number | null
  lift: number | null
  /** Abstand zum Erwartungswert in Standardabweichungen. */
  z: number | null
  /** Nur gesetzt, wenn das Paar die Schranke für die Zahl der
   *  geprüften Paare nimmt. */
  auffaellig: 'MEHR' | 'WENIGER' | null
}
export interface WarenkorbBild {
  von: string; bis: string
  ebene: 'gruppe' | 'artikel'
  /** Tischbelegungen im Zeitraum — der Korb ist die Tischbelegung. */
  koerbe: number
  speisenkoerbe: number
  inhalte: number
  je_korb: number | null
  mindestbasis: number
  sachen: KorbSache[]
  /** Wie viele Paare überhaupt vorkamen. */
  paare: number
  /** Wie viele davon eine Basis haben, auf der sich prüfen lässt. */
  pruefbar: number
  auffaellig: number
  schranke: number | null
  zeilen: KorbPaar[]
}

export interface TagesBild {
  tag: string
  kennzahlen?: Kennzahlen
  vergleich?: Vergleich
  stunden?: Stunde[]
  zahlarten?: Zahlart[]
  zahlarten_umsatz?: UmsatzZahlart[]
  zahlungsbericht_vorhanden?: boolean
  gutscheine?: { kauf: Betrag; einloesung: Betrag }
  speisen_getraenke?: SpeisenGetraenke
  warengruppen?: Warengruppe[]
  renner?: Artikel[]
  penner?: Artikel[]
  stornos?: Stornos
  ampel?: AmpelTag[]
  tagesabschluss?: { vorhanden: number; erwartet: number }
  debitoren?: { betrag: Betrag; erwartet: number; vorhanden: number }
}

export interface AbbruecheBild {
  von: string
  bis: string
  verlauf?: { tag: string; vorgaenge: number; kreise: number }[]
  stunden?: { stunde: number; vorgaenge: number; kreise: number }[]
  artikel?: {
    eindeutig: number; block: number; ohne_zeitnahe_buchung: number; ohne: number
    artikel: {
      artikel: string; abbrueche: number; positionen: number
      menge: Betrag; summe: Betrag
    }[]
  }
  mitarbeiter?: {
    person_id: string; kennung: string; vorgaenge: number; anteil: number
  }[]
  personen_gefragt: boolean
  mit_personen: boolean
}

export interface VerlaufBild {
  von: string; bis: string
  tage: Tag[]; stunden: Stunde[]; wochentage: Wochentag[]
}

export interface ArtikelBild {
  renner?: Artikel[]; penner?: Artikel[]
  warengruppen?: Warengruppe[]
  karteileichen?: { article_ref: string; artikel: string; zuletzt: string | null }[]
}

export interface QuotenTag {
  tag: string
  umsatz: Betrag
  rechnungen: number; tischbesuche: number; betriebe: number
  gebucht: Betrag; storno_summe: Betrag; storno_anzahl: number
  sofort_wert: Betrag; sofort_anzahl: number
  rabatt_summe: Betrag; rabatt_anzahl: number
  trinkgeld: Betrag; bar: Betrag; positionen: number
  durchschnittsbon: Betrag
  stornoquote: number | null;    stornoquote_7t: number | null;    stornoquote_auflaufend: number | null
  rabattquote: number | null;    rabattquote_7t: number | null;    rabattquote_auflaufend: number | null
  trinkgeldquote: number | null; trinkgeldquote_7t: number | null; trinkgeldquote_auflaufend: number | null
  barquote: number | null;       barquote_7t: number | null;       barquote_auflaufend: number | null
  sofort_je_100: number | null;  sofort_je_100_7t: number | null
  storno_je_100: number | null
  umsatz_auflaufend: Betrag; rechnungen_auflaufend: number
}

export interface QuoteBeschreibung {
  schluessel: string
  name: string
  zaehler: string
  nenner: string
  einheit: string
  hoch_ist_gut: boolean | null
  erklaerung: string
}

export interface Abweichung {
  betrieb: string; location_id: string; kennzahl: string
  wert: Betrag; normal: Betrag; streuung: Betrag
  vergleichstage: number; abstand: number; richtung: 'hoch' | 'niedrig'
}

export interface StandortZeile {
  id: string; betrieb: string; code: string
  tage: number; umsatz: Betrag; rechnungen: number
  durchschnittsbon: Betrag
  umsatz_je_tag: Betrag
  stornoquote: number | null; rabattquote: number | null
  trinkgeldquote: number | null; barquote: number | null
  sofort_je_100: number | null; sofort_anzahl: number
  anteil: number | null
}

export interface GaesteTag {
  tag: string; gaeste: number; gaeste_bereinigt: number
  tischbesuche: number; tischbesuche_bereinigt: number; belege: number
  umsatz: Betrag; umsatz_je_gast: Betrag
  umsatz_mit_gaesten: Betrag; gaeste_positiv: number
  verkaeufe_mit_gaesten: number; gaeste_auf_verkaeufen: number
  belege_mit_tisch: number; belege_fuer_tisch: number
  gaeste_je_tisch: number | null; tischdauer_median: number | null
  tische: number; splits: number; splitquote: number | null
}
export interface Bereich {
  bereich: string; tische: number; besuche: number
  gaeste: number; umsatz: Betrag; umsatz_je_gast: Betrag
}
export interface TischZeile {
  tisch: string; betrieb_id: string; betrieb: string
  bereich: string | null; besuche: number; gaeste: number
  umsatz: Betrag; umsatz_je_besuch: Betrag
  umschlag: number | null; dauer_median: number | null
}
export interface Tischgroesse {
  gaeste: number; belege: number; umsatz: Betrag; durchschnittsbon: Betrag
}
export interface Belegart {
  art: string; anzahl: number; umsatz: Betrag; anteil: number | null
}
export interface Tischdauer {
  median: number | null; besuche: number; drei_viertel: number | null
}
export interface Quellenabgleich {
  quittungen: number; mit_beleg: number; ohne_beleg: number; mehrdeutig: number
  summe_quittung: Betrag | null; summe_beleg: Betrag | null
  betrag_weicht_ab: number | null
}
export interface GaesteBild {
  von: string; bis: string; vorhanden: boolean
  tage: GaesteTag[]; tischdauer: Tischdauer
  bereiche: Bereich[]; tische: TischZeile[]
  tischgroessen: Tischgroesse[]; belegarten: Belegart[]
  quellenabgleich: Quellenabgleich
}

export interface StornoKopf {
  stornos: number; summe: Betrag; ohne_preis: number
  wiedereroeffnet: number; belege: number; tage: number
  quelltage: number; tage_gesamt: number
  quote_wiedereroeffnet: number | null
  je_hundert_rechnungen: number | null
}
export interface StornoTag {
  tag: string; stornos: number; summe: Betrag
  wiedereroeffnet: number; belege: number
  je_hundert_rechnungen: number | null
}
export interface Stornogrund {
  grund: string; anzahl: number; summe: Betrag
  wiedereroeffnet: number; anteil: number | null
}
export interface StornoArtikel {
  artikel: string; anzahl: number; summe: Betrag; ohne_preis: number
}
export interface StornoStunde { stunde: number; anzahl: number; summe: Betrag }
export interface StornoBeleg {
  beleg: string; betrieb_id: string; betrieb: string; tag: string
  tisch: string | null; stornos: number; summe: Betrag
  wiedereroeffnet: number; von: string; bis: string; gruende: number
}
export interface StornoPerson {
  person_id: string; kennung: string; herkunft: string
  stornos: number; summe: Betrag; wiedereroeffnet: number
  belege: number; fuer_eroeffner: number; tage: number
  je_storno: Betrag; anteil: number | null
}
export interface StornoGrundPerson {
  person_id: string; kennung: string; grund: string
  anzahl: number; summe: Betrag
}
export interface StornoBild {
  von: string; bis: string; vorhanden: boolean
  kopf: StornoKopf; tage: StornoTag[]; gruende: Stornogrund[]
  artikel: StornoArtikel[]; stunden: StornoStunde[]; belege: StornoBeleg[]
  mit_personen: boolean
  /** Ob der Aufrufer den Personenteil überhaupt angefragt hat. Das
   *  Reporting tut es nicht — dort wird er nicht ausgeblendet, sondern
   *  gar nicht erst gelesen. */
  personen_gefragt: boolean
  personen: StornoPerson[]; gruende_je_person: StornoGrundPerson[]
}

export interface BefundKopf {
  befunde: number; punkte: number; verdraengt: number
  personen: number; regeln: number; belege: number; betrag: Betrag
}
export interface BefundLauf {
  tage: number; von: string | null; bis: string | null
  zuletzt: string | null; fassung: string | null
  regeln: number; dauer_ms: number
}
export interface BefundRegel {
  regel: string; name: string; kategorie: string
  punkte_je_fall: number; anzahl: number; punkte: number; verdraengt: number
}
export interface BefundPerson {
  person_id: string; kennung: string; herkunft: string
  befunde: number; punkte: number; regeln: number; tage: number
  ueber_abschliesser: number; schwerster: number
}
export interface BefundZeile {
  id: string; tag: string; regel: string; regel_name: string; kategorie: string
  punkte: number; punkte_gewertet: number; status: string
  verdraengt_von: string | null; sicherheit: string
  zurechnung: string | null; regel_zurechnung: string
  kennung: string | null; person_id: string | null
  beleg: string | null; tisch: string | null; betrag: Betrag
  uhrzeit: string | null; betrieb: string; betrieb_id: string
  erklaerung: string; belege: Record<string, unknown>
}
export interface KatalogRegel {
  code: string; kategorie: string; name: string; beschreibung: string
  punkte: number; gebaut: boolean; fehlende_quelle: string | null
  aktiv: boolean; muster: boolean; verdraengt: string[]
  bezugsgroesse: string | null; mindestbasis: number | null
}
export interface BefundBild {
  von: string; bis: string; geprueft: boolean
  lauf: BefundLauf; kopf: BefundKopf; tage: { tag: string; befunde: number; punkte: number; personen: number }[]
  regeln: BefundRegel[]; personen: BefundPerson[]
  liste: BefundZeile[]; katalog: KatalogRegel[]
}

export interface ZusammenKopf {
  nummer: string
  artikel: string
  anzahl: number
  umsatz: Betrag
}

export interface ZusammenWahl {
  nummer: string
  artikel: string
  /** Die Abfrage der Kasse, unter der die Wahl steht — „Gewicht Arg.
   *  Filet", „Dip Auswahl". Leer, solange keine Stammdaten vorliegen. */
  abfrage: string | null
  anzahl: number
  umsatz: Betrag
}

export interface ZusammenBild {
  zeitraum: { von: string; bis: string }
  artikel: ZusammenKopf[]
  anteil: {
    umsatz_zusammenstellungen: Betrag
    anzahl: number
    umsatz_gesamt: Betrag
  }
  beistellungsgruppen: { name: string; quelle: string }[]
  hinweis: string
}

export interface ZusammenDetail {
  kopf: ZusammenKopf
  abfragen: Record<string, ZusammenWahl[]>
}


/* Rechnungen und ihr Buchungsjournal.
 *
 * `von`/`bis` und `zeit` sind **Text in Ortszeit**, keine Zeitstempel.
 * Ein timestamptz verlaesst die API als UTC, und dann steht 17:39 als
 * 15:39 in der Zeile — an einer Stelle, an der es plausibel aussieht.
 */
export interface Rechnung {
  allocation_key: string
  location_id: string
  account_name: string | null
  table_no: string | null
  table_part: string | null
  area: string | null
  von: string
  bis: string
  zeilen: number
  gebucht: Betrag | null
  buchungen: number
  stornos: number
  storno_summe: Betrag | null
  rabatt: Betrag | null
  raus: number
  rein: number
  fremd: number
  bediener: string[] | null
  /** Sofortstornos auf diesem Konto — **gezaehlt im Journal**
   *  (`SALE` mit Menge 0), damit die Zahl auch ohne Archiv stimmt. */
  sofort_anzahl: number
  /** Wie viele davon einen Preis tragen. Kopfartikel, Abwahlen und
   *  Kuechenanweisungen haben nie einen. */
  sofort_mit_preis: number
  /** Die Summe der Einzelpreise — **nur aus dem Archiv**, sonst null.
   *  Sie ist kein Umsatz: Gebucht wurden 0,00 €. */
  sofort_summe: Betrag | null
  bezahlt: Betrag | null
  trinkgeld: Betrag | null
  zahlarten: string | null
  zahlung_besonders: number | null
  /** Das Konto, aus dem die Positionen kamen — ueber die Umbuchung
   *  ermittelt, nicht ueber den Tischnamen. */
  haupt: string | null
  /** `SPLIT` = Teilrechnung derselben Tischbelegung, wird eingerueckt.
   *  `FOREIGN` = Fremdbuchung auf einen anderen Tisch, steht fuer sich. */
  haupt_art: 'SPLIT' | 'FOREIGN' | null
  haupt_name: string | null
}

export interface Journalzeile {
  zeit: string
  /** Trennt die beiden Haelften: Buchungszeile oder Zahlzeile. `VOID`
   *  heisst auf beiden Seiten etwas anderes — dort ein stornierter
   *  Artikel, hier eine stornierte Zahlung. */
  ist_zahlung: boolean
  row_no: number
  art: string
  article_text: string | null
  group_name: string | null
  quantity: Betrag | null
  unit_gross: Betrag | null
  line_gross: Betrag | null
  discount: Betrag | null
  breakage: Betrag | null
  free: Betrag | null
  operator_ref: string | null
  device: string | null
  method_name: string | null
  tip: Betrag | null
  zahlart_kind: string | null
  /** Der Einzelpreis eines Sofortstornos, aus dem Archiv. Null, wenn
   *  die Zeile keiner ist — oder wenn der Artikel nie einen Preis
   *  getragen hat (Kopfartikel, „ohne …", „NACH …"). */
  sofort_preis: Betrag | null
  gegenkonto: string | null
  gegenkonto_ref: string | null
}

export interface Rechnungsbild {
  tag: string
  rechnungen: Rechnung[]
  journal_vorhanden: boolean
  /** Ohne Archiv fehlt der **Preis** des Sofortstornos, nicht er selbst. */
  archiv_vorhanden: boolean
}


export interface EinrichtungHinweis {
  art: 'OFFEN' | 'PRUEFEN' | 'HINWEIS'
  text: string
  artikel: { ref: string; name: string }[]
  anzahl: number
}

export interface EinrichtungStandort {
  code: string
  name: string
  mit_kasse: boolean
  dateimuster: string | null
  muster_quelle: 'KUNDE' | 'ORT' | null
  herkunft: 'BACKOFFICE' | 'AUTOMATISCH'
  bestaetigt: boolean
  dateien: number
  letzter_tag: string | null
}

export interface EinrichtungZahlart {
  name: string
  /** Der rohe ZAHLART_TYP aus dem Archiv. Zahlungsberichte liefern
   *  keinen Typ; auch alte Archivbuchungen können ohne Typ vorliegen. */
  typ: string | null
  art: string
  art_quelle: string
  anzeige_name: string | null
  first_seen: string | null
  last_seen: string | null
  typ_unbekannt: boolean
  nicht_eingeordnet: boolean
}

export interface EinrichtungRabattart {
  name: string
  freigabe: 'OFFEN' | 'BESTAND' | 'KUNDE'
  first_seen: string | null
  last_seen: string | null
}

export interface EinrichtungArtikel {
  article_ref: string
  name: string
  rolle: string
  freitext: boolean
  freitext_quelle: string
  last_seen: string | null
}

export interface EinrichtungStation {
  location_id: string
  standort: string
  terminal_ref: string
  name: string | null
  abgeleiteter_name: string | null
  name_quelle: 'ABGELEITET' | 'KUNDE'
  first_seen: string | null
  last_seen: string | null
  positionen: number
  umsatz: Betrag
  positionswert: Betrag
  groesste_warengruppe: string | null
  vorschlag_grund: string | null
}

export interface StationBasis {
  location_id: string
  standort: string
  terminal_ref: string | null
  station: string
  benannt: boolean
}
export interface StationZeile extends StationBasis {
  positionen: number
  umsatz: Betrag
  positionswert: Betrag
}
export interface StationWarengruppe extends StationBasis {
  warengruppe: string; positionen: number; umsatz: Betrag; anteil: number
}
export interface StationVerlauf extends StationBasis { stunde: number; umsatz: Betrag }
export interface StationenBild {
  von: string; bis: string
  stationen?: StationZeile[]
  warengruppen?: StationWarengruppe[]
  verlauf?: StationVerlauf[]
}

export interface EinrichtungBild {
  betriebe: EinrichtungStandort[]
  stammdaten: {
    gelesen_am: string
    filename: string
    artikel: number
    koepfe: number
    abfragen: number
    pflichtabfragen: number
    nur_ueber_abfrage: number
    warengruppen: number
    mit_art: number
    ohne_art: number
  } | null
  quellen: { quelle: string; dateien: number; zuletzt: string; tage: number }[]
  zahlarten: EinrichtungZahlart[]
  rabattarten: EinrichtungRabattart[]
  artikel: EinrichtungArtikel[]
  stationen: EinrichtungStation[]
  warengruppen: { art: string; gruppen: number; artikel: number }[]
  zerlegung: {
    aus_stammdaten: number
    abfragen: number
    unbekannt: number
    nur_ueber_abfrage: number
    gebuchte_koepfe: number
  }
  beistellungsgruppen: { name: string; quelle: string }[]
  bestandteile: {
    kanten: number
    eltern: number
    koepfe: number
    zuordnungen: number
    abfragen: number
  }
  offen: EinrichtungHinweis[]
}


export class ApiFehler extends Error {
  constructor(message: string, readonly status: number) {
    super(message)
  }
}

/* Anmeldung und Verwaltung.
 *
 * Das Sitzungsmerkmal steht in einem HttpOnly-Cookie. Kein Skript hier
 * kann es lesen — und genau das ist der Punkt: Eine einzige
 * eingeschleuste Zeile könnte es sonst mitnehmen. Der Browser schickt
 * es bei jeder Anfrage an denselben Ursprung von selbst mit.
 */

export interface Ich {
  angemeldet: boolean
  email?: string
  name?: string
  rolle?: 'ADMIN' | 'INHABER' | 'OPERATIVE' | 'BUCHHALTUNG' | 'BETRIEBSLEITER' | 'MITARBEITER'
  mandant?: string | null
  /* Nur einmal, gleich nach dem Einrichten des zweiten Faktors. */
  ersatzcodes?: string[]
}

/* Das Passwort stimmte, es fehlt ein Schritt.
 *
 * `einrichtung` ist die `otpauth://`-Adresse für den QR-Kode — sie
 * trägt das Geheimnis im Klartext und kommt genau einmal, beim
 * Einrichten. Danach steht dort `null`: Käme sie jedes Mal, genügte
 * eine abgefangene Antwort, um den zweiten Faktor mitzunehmen.
 */
export interface Schritt {
  zweiter_faktor: 'PRUEFEN' | 'EINRICHTEN'
  einrichtung: string | null
  minuten: number
}

export function istSchritt(a: Ich | Schritt): a is Schritt {
  return (a as Schritt).zweiter_faktor !== undefined
}

export interface Faktorzustand {
  gilt: boolean
  vorbereitet: boolean
  pflicht: boolean
  ersatzcodes: number
}

export interface Zugang {
  id: string
  email: string
  name: string
  rolle: string
  bezeichnung: string
  rang: number
  aktiv: boolean
  /** Hat der Zugang schon ein Passwort? Eine Einladung allein reicht nicht. */
  eingerichtet: boolean
  letzte_anmeldung: string | null
  betriebe: string[]
  schaltbar: boolean
}

export interface Stufe {
  rolle: string
  bezeichnung: string
  rang: number
  /** ALLE · WAEHLBAR · GENAU_EINER */
  betriebe: string
}

export interface Standort {
  id: string
  code: string
  name: string
  strasse: string | null
  plz: string | null
  ort: string | null
  ust_id: string | null
  kassenkennung: string | null
  dateimuster: string | null
  muster_quelle: string | null
  herkunft: string
  bestaetigt_am: string | null
  geschaeftstag_ab: string
  zeitzone: string
  aktiv: boolean
  tage: number
  letzter_tag: string | null
}

/** Was ein Standortformular schickt.
 *
 *  `undefined` heißt „nicht anfassen", die leere Zeichenkette heißt
 *  „leeren". Ohne den Unterschied gäbe es keinen Weg, eine falsch
 *  eingetragene Kassenkennung wieder loszuwerden.
 */
export interface StandortEingabe {
  name?: string
  strasse?: string | null
  plz?: string | null
  ort?: string | null
  ust_id?: string | null
  kassenkennung?: string | null
  dateimuster?: string | null
  geschaeftstag_ab?: string | null
  zeitzone?: string | null
  aktiv?: boolean
}

export interface Zugangsbild {
  zugaenge: Zugang[]
  stufen: Stufe[]
  betriebe: { id: string; code: string; name: string; active: boolean }[]
  ich: string
}

export interface Einladung {
  name: string
  email: string
  bezeichnung: string
  laeuft_ab_am: string
}

export interface ImportDatei {
  name: string
  art: string
  /** GELADEN · DOPPELT · UEBERSPRUNGEN · FEHLER */
  zustand: string
  meldung: string
  warnungen: string[]
  datenzeilen: number
  uebersprungen: number
}

export interface OffeneGruppe {
  name: string
  schluessel: string
  zeilen: number
  steuerprofil: string | null
  artikel: number
}

export interface Importbild {
  dateien: ImportDatei[]
  neue_betriebe: string[]
  /** Neu angelegte Standorte ohne Dateimuster — mit dem Grund. */
  ohne_muster: { standort: string; grund: string }[]
  /** Zahlarten, deren Typ das System nicht kennt — als „Name (Typ)". */
  zahlarten_offen: string[]
  tage: string[]
  ohne_archiv: FehlendesArchiv[]
  zahlen: {
    geladen: number; doppelt: number; uebersprungen: number; fehler: number
  }
  offene_warengruppen: OffeneGruppe[]
}

export interface FehlendesArchiv {
  betrieb: string
  standort: string
  tag: string
  berichte: string[]
}

export interface VerwMandant {
  id: string
  code: string
  name: string
  status: string
  module_dashboard: boolean
  module_audit: boolean
  dimension_employee: boolean
  dpa_signed_at: string | null
  betriebe: number
  dateien: number
  letzter_tag: string | null
}

export interface Warengruppe {
  id: string
  code: string
  name: string
  kind: 'FOOD' | 'BEVERAGE' | 'OTHER' | 'UNKNOWN'
  steuerprofil: string | null
  steuersatz: string | null
  art_quelle: 'KUNDE' | 'ABGELEITET'
  herkunft: 'KASSE' | 'BACKOFFICE'
  aktiv: boolean
  artikel: number
  /* Wie viele Datensätze diesen Namen tragen. Normalerweise einer —
   * zwei, wenn die Artikelliste vor dem Archiv gelesen wurde und beide
   * Lader je eine Zeile angelegt haben. */
  zeilen: number
}

export interface WgAbgleich {
  neu: { name: string; steuerprofil: string | null; steuersatz: number | null
         artikel_kasse: number | null }[]
  bekannt: { name: string; code: string; kind: string; artikel: number
             steuerprofil_alt: string | null; steuerprofil: string | null
             steuersatz: number | null; artikel_kasse: number | null
             aendert_sich: boolean }[]
  nicht_eingefuegt: { name: string; code: string; kind: string
                      artikel: number; herkunft: string }[]
  uebersprungen: string[]
  uebernommen: boolean
  zahlen: { gelesen: number; neu: number; bekannt: number
            nicht_eingefuegt: number }
}

export interface Hochgeladen {
  datei: string
  bytes: number
  doppelt: boolean
  hinweis: string | null
  artikel: number
  neu: number
  abfragen: number
  warengruppen: number
  bestandteile: number
  meldung: string
}

async function schicke<T>(pfad: string, leib?: unknown): Promise<T> {
  const antwort = await fetch(`/api${pfad}`, {
    method: 'POST',
    headers: leib === undefined ? {} : { 'Content-Type': 'application/json' },
    body: leib === undefined ? undefined : JSON.stringify(leib),
  })
  if (!antwort.ok) throw new ApiFehler(await meldung(antwort), antwort.status)
  return antwort.json() as Promise<T>
}

async function meldung(antwort: Response): Promise<string> {
  let text = `Die Anfrage ist fehlgeschlagen (${antwort.status}).`
  try {
    const j = await antwort.json()
    if (j?.detail) text = typeof j.detail === 'string' ? j.detail : text
  } catch { /* Antwort ohne JSON - dann bleibt der allgemeine Text */ }
  return text
}

async function hole<T>(pfad: string, suche?: Record<string, string | string[] | undefined>): Promise<T> {
  const u = new URLSearchParams()
  for (const [k, v] of Object.entries(suche ?? {})) {
    if (v === undefined) continue
    if (Array.isArray(v)) v.forEach((x) => u.append(k, x))
    else u.set(k, v)
  }
  const frage = u.toString()
  const antwort = await fetch(`/api${pfad}${frage ? '?' + frage : ''}`)
  if (!antwort.ok) {
    throw new ApiFehler(await meldung(antwort), antwort.status)
  }
  return antwort.json() as Promise<T>
}

const orte = (b?: string[]) => (b && b.length ? { betrieb: b } : {})

export const api = {
  kontext: () => hole<Kontext>('/kontext'),
  aenderungen: () => hole<Aenderung[]>('/aenderungen'),
  auswertungen: (ansicht?: string) =>
    hole<Auswertung[]>('/auswertungen', { ansicht }),
  ansichten: (werkzeug?: 'REPORTING' | 'AUDIT') =>
    hole<Auswertungsansicht[]>('/ansichten', { werkzeug }),

  ich: () => hole<Ich>('/ich'),
  anmelden: (email: string, passwort: string, kennung?: string) =>
    schicke<Ich | Schritt>('/anmeldung', { kennung, email, passwort }),

  /* Der zweite Schritt. Das Merkmal steht im Cookie und nicht hier —
   * es ist HttpOnly, kein Skript kommt daran, und der Browser schickt
   * es von allein mit. */
  kennzahl: (kennzahl: string) =>
    schicke<Ich>('/anmeldung/kennzahl', { kennzahl }),

  abmelden: () => schicke<{ abgemeldet: boolean }>('/abmeldung'),

  faktor: {
    zustand: () => hole<Faktorzustand>('/ich/faktor'),
    beginnen: () => schicke<{ einrichtung: string }>('/ich/faktor/beginnen'),
    bestaetigen: (kennzahl: string) =>
      schicke<{ gilt: boolean; ersatzcodes: string[] }>(
        '/ich/faktor/bestaetigen', { kennzahl }),
    abschalten: (passwort: string) =>
      schicke<{ gilt: boolean }>('/ich/faktor/abschalten', { passwort }),
    ersatzcodes: (passwort: string) =>
      schicke<{ ersatzcodes: string[] }>('/ich/faktor/ersatzcodes', { passwort }),
  },

  verwaltung: {
    mandanten: () => hole<{ mandanten: VerwMandant[] }>('/verwaltung/mandanten'),
    anlegen: (code: string, name: string) =>
      schicke<{ id: string }>('/verwaltung/mandanten', { code, name }),
    aendern: (id: string, daten: Record<string, unknown>) =>
      schicke<{ geaendert: boolean }>(`/verwaltung/mandant/${id}`, daten),
    waehlen: (id: string) =>
      schicke<{ mandant: string }>(`/verwaltung/waehlen/${id}`),
    warengruppen: () =>
      hole<{ warengruppen: Warengruppe[] }>('/verwaltung/warengruppen'),
    einfuegen: (text: string, uebernehmen: boolean) =>
      schicke<WgAbgleich>('/verwaltung/warengruppen/einfuegen',
                          { text, uebernehmen }),
    art: (name: string, art: string) =>
      schicke<{ gesetzt: boolean }>('/verwaltung/warengruppe/art', { name, art }),

    artikelliste: async (datei: File) => {
      // Kein Content-Type von Hand: Den setzt der Browser samt Grenze
      // selbst, und eine selbst gesetzte Grenze passt nie.
      const formular = new FormData()
      formular.append('datei', datei)
      const antwort = await fetch('/api/verwaltung/artikelliste',
                                  { method: 'POST', body: formular })
      if (!antwort.ok) throw new ApiFehler(await meldung(antwort), antwort.status)
      return antwort.json() as Promise<Hochgeladen>
    },
    auswertungSetzen: (schluessel: string, aktiv: boolean) =>
      schicke<{ gespeichert: boolean }>(
        `/verwaltung/auswertung/${encodeURIComponent(schluessel)}`, { aktiv }),
    ansichtSetzen: (ansicht: string, aktiv: boolean) =>
      schicke<{ gespeichert: boolean }>(
        `/verwaltung/ansicht/${encodeURIComponent(ansicht)}`, { aktiv }),
    auswertungBreiteSetzen: (schluessel: string, breite: 'viertel' | 'halb' | 'voll') =>
      schicke<{ gespeichert: boolean }>(
        `/verwaltung/auswertung/${encodeURIComponent(schluessel)}`, { breite }),
    auswertungenOrdnen: (ansicht: string, schluessel: string[]) =>
      schicke<{ gespeichert: boolean }>(
        `/verwaltung/auswertungen/${encodeURIComponent(ansicht)}/ordnen`,
        { schluessel }),
    auswertungenZuruecksetzen: (ansicht: string) =>
      schicke<{ zurueckgesetzt: boolean }>(
        `/verwaltung/auswertungen/${encodeURIComponent(ansicht)}/zuruecksetzen`),
  },

  /* Das Kundenbackoffice: Zugänge, Import, offene Warengruppen. */
  haus: {
    standorte: () => hole<{ standorte: Standort[] }>('/haus/standorte'),
    standortAnlegen: (daten: StandortEingabe) =>
      schicke<{ id: string }>('/haus/standorte', daten),
    standortPflegen: (id: string, daten: StandortEingabe) =>
      schicke<{ gespeichert: boolean }>(`/haus/standort/${id}`, daten),

    zugaenge: () => hole<Zugangsbild>('/haus/zugaenge'),
    anlegen: (email: string, name: string, rolle: string, betriebe: string[]) =>
      schicke<{ id: string; einladung: string; link: string; tage: number }>(
        '/haus/zugaenge', { email, name, rolle, betriebe }),
    schalten: (id: string, aktiv: boolean) =>
      schicke<{ aktiv: boolean }>(`/haus/zugang/${id}`, { aktiv }),
    einladungNeu: (id: string) =>
      schicke<{ einladung: string; link: string; tage: number }>(
        `/haus/zugang/${id}/einladung`),

    /* Der tägliche Import. Mehrere Dateien auf einmal — abends liegen
     * vier nebeneinander, und sie einzeln hochzuladen wäre viermal
     * dieselbe Handlung. */
    importieren: async (dateien: File[]) => {
      const formular = new FormData()
      for (const d of dateien) formular.append('dateien', d)
      const antwort = await fetch('/api/import',
                                  { method: 'POST', body: formular })
      if (!antwort.ok) throw new ApiFehler(await meldung(antwort), antwort.status)
      return antwort.json() as Promise<Importbild>
    },

    offeneWarengruppen: () =>
      hole<{ offen: OffeneGruppe[] }>('/warengruppen/offen'),
    warengruppeSetzen: (name: string, art: string) =>
      schicke<{ gesetzt: boolean }>('/warengruppen/offen', { name, art }),
  },

  /* Die Einladung — die einzigen beiden Wege ohne Anmeldung. */
  einladung: {
    ansehen: (merkmal: string) =>
      hole<Einladung>(`/einladung/${encodeURIComponent(merkmal)}`),
    /* Auch hier kann ein zweiter Faktor dazwischenstehen — dann
     * nämlich, wenn die Stufe des eingeladenen Kontos einen verlangt.
     * Ohne diesen Fall wäre eine neue Einladung der Weg daran vorbei. */
    einloesen: (merkmal: string, passwort: string) =>
      schicke<Ich | Schritt>('/einladung', { merkmal, passwort }),
  },

  warenkorb: (von?: string, bis?: string, betrieb?: string[],
              ebene: 'gruppe' | 'artikel' = 'gruppe') =>
    hole<WarenkorbBild>('/warenkorb', { von, bis, ebene, ...orte(betrieb) }),
  tag: (datum?: string, betrieb?: string[], auswertung?: string[]) =>
    hole<TagesBild>('/tag', { datum, auswertung, ...orte(betrieb) }),
  verlauf: (von?: string, bis?: string, betrieb?: string[]) =>
    hole<VerlaufBild>('/verlauf', { von, bis, ...orte(betrieb) }),
  artikel: (von?: string, bis?: string, betrieb?: string[], nach: 'umsatz' | 'menge' = 'umsatz', auswertung?: string[]) =>
    hole<ArtikelBild>('/artikel', { von, bis, nach, auswertung, grenze: '25', ...orte(betrieb) }),
  stornos: (von?: string, bis?: string, betrieb?: string[]) =>
    hole<Stornos>('/stornos', { von, bis, ...orte(betrieb) }),
  datenqualitaet: (von?: string, bis?: string, betrieb?: string[]) =>
    hole<{ tage: AmpelTag[] }>('/datenqualitaet', { von, bis, ...orte(betrieb) }),
  offeneArchive: () =>
    hole<{ tage: FehlendesArchiv[] }>('/datenqualitaet/offene-archive'),
  pruefungen: (datum: string, betrieb: string) =>
    hole<Pruefung[]>('/pruefungen', { datum, betrieb }),
  quoten: (von?: string, bis?: string, betrieb?: string[], auswertung?: string[]) =>
    hole<{ von: string; bis: string; reihe?: QuotenTag[];
           katalog?: QuoteBeschreibung[]; befunde?: Abweichung[] }>(
      '/quoten', { von, bis, auswertung, ...orte(betrieb) }),
  abweichungen: (datum?: string, betrieb?: string[]) =>
    hole<{ tag: string; schwelle: number; befunde: Abweichung[] }>(
      '/abweichungen', { datum, ...orte(betrieb) }),
  gaeste: (von?: string, bis?: string, betrieb?: string[]) =>
    hole<GaesteBild>('/gaeste', { von, bis, ...orte(betrieb) }),
  // `personen` ist voreingestellt aus, hier wie im Server: Der
  // Personenteil gehoert zum Audit und wird nur auf Nachfrage gelesen.
  stornoreport: (von?: string, bis?: string, betrieb?: string[],
                 personen = false) =>
    hole<StornoBild>('/stornoreport',
      { von, bis, personen: personen ? 'true' : 'false', ...orte(betrieb) }),
  befunde: (von?: string, bis?: string, betrieb?: string[]) =>
    hole<BefundBild>('/befunde', { von, bis, ...orte(betrieb) }),
  rechnungen: (tag?: string, betrieb?: string[]) =>
    hole<Rechnungsbild>('/rechnungen', { tag, ...orte(betrieb) }),
  rechnung: (konto: string, tag?: string, betrieb?: string[]) =>
    hole<{ konto: string; tag: string; journal: Journalzeile[] }>(
      `/rechnung/${encodeURIComponent(konto)}`, { tag, ...orte(betrieb) }),
  einrichtung: () => hole<EinrichtungBild>('/einrichtung'),
  zahlartArtSetzen: (name: string, art: string) =>
    schicke<{ gesetzt: boolean }>('/zahlart/art', { name, art }),
  rabattartFreigeben: (name: string) =>
    schicke<{ gesetzt: boolean }>('/rabattart/freigabe', { name }),
  zahlartZusammenfassen: (name: string, ziel: string | null) =>
    schicke<{ gesetzt: boolean }>('/zahlart/zusammenfassen', { name, ziel }),
  artikelFreitextSetzen: (article_ref: string, freitext: boolean) =>
    schicke<{ gesetzt: boolean }>('/artikel/freitext', { article_ref, freitext }),
  stationBenennen: (location_id: string, terminal_ref: string, name: string | null) =>
    schicke<{ gesetzt: boolean }>('/station/name', { location_id, terminal_ref, name }),
  stationen: (von?: string, bis?: string, betrieb?: string[], auswertung?: string[]) =>
    hole<StationenBild>('/stationen', { von, bis, auswertung, ...orte(betrieb) }),
  abbrueche: (von?: string, bis?: string, betrieb?: string[],
              auswertung?: string[], personen = false) =>
    hole<AbbruecheBild>('/abbrueche', {
      von, bis, auswertung, personen: personen ? 'true' : 'false', ...orte(betrieb),
    }),
  zusammenstellungen: (von?: string, bis?: string, betrieb?: string[]) =>
    hole<ZusammenBild>('/zusammenstellungen', { von, bis, ...orte(betrieb) }),
  zusammenstellung: (artikel: string, von?: string, bis?: string, betrieb?: string[]) =>
    hole<ZusammenDetail>('/zusammenstellung', { artikel, von, bis, ...orte(betrieb) }),
  betriebe: (von?: string, bis?: string) =>
    hole<{ von: string; bis: string; betriebe: StandortZeile[] }>('/betriebe', { von, bis }),
}
